import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Button,
  Chip,
  CircularProgress,
  makeStyles,
  Modal,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  UserType,
  useActivateMemberProfileAndPerksBannersForChamberMemberMutation,
  GetAllChamberMembers_RootAndChamberAdminOnlyDocument,
  useGetAllChamberMembers_RootAndChamberAdminOnlyQuery,
  ChamberMemberReturn,
  useDeactivateMemberProfileAndPerksBannersForChamberMemberMutation,
  useReactivateMemberProfileAndPerksBannersForChamberMemberMutation,
  ActiveMemberProfileAndPerksBannersSettingsActivationInfoReturn,
  GetUser_RootAdminChamberAdminQuery,
} from "../../../graphql/types";
import { ChamberMemberCategory } from "../../../graphql/types";
import { useGetCategoryIdToHumanReadableName } from "../../common/Hooks/useGetCategoryIdToHumanReadableName";
import { Select, MenuItem, FormControl } from "@material-ui/core";
import Loader from "../../common/Loader/Loader";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { goldDatePickerTheme } from "../../../themes";
import Box from "@mui/material/Box";
import { TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from "@material-ui/icons/Close";
import {
  activationFormStyles,
  FormContainer,
  InfoTextSection,
  FormLabel,
  AnnualFeeDisplay,
} from "../../common/ChamberEngageActivationForm";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import { useHistory, useLocation } from "react-router-dom";
import { UserTypeContext } from "../../../UserTypeContext";
import { getLocalTimestamp } from "../Root/RootAdmin";
import {
  getExpiryUtcMilliTimestampThatIsSpecifiedMonthsAfterSpecifiedAdvertisingStartDateUtcMilli,
  useGetGraphqlChamberIdVariableObjForRootAdmin,
} from "./ManageChamberEngageCategoryAdvertisingBanners";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import EventIcon from "@material-ui/icons/Event";
import UilExportIcon from "../../../images/uil_export.png";
import FilterIcon from "../../../images/filter.png";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import CommonTable, { HeadCell } from "../../common/CommonTable";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import SuccessDialog from "../../common/SuccessDialog";
import ComingSoonDialog from "../../common/ComingSoonDialog";

const numberOfMonthsTillExpiry = 12;

// Activation Form Component

interface ActivationFormProps {
  chamberInfo:
  | GetUser_RootAdminChamberAdminQuery["getUser"]["chamberInfo"]
  | undefined;
  allChamberMembers: any[]; // Using any[] for now to match the complex return type, or refine to ChamberMemberReturn[]
  refetch: () => void;
  isLoadingMembers?: boolean;
}

const ActivationForm: React.FC<ActivationFormProps> = ({
  chamberInfo,
  allChamberMembers,
  refetch,
  isLoadingMembers = false,
}) => {
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const location = useLocation();
  const preselectedMemberId = (location?.state as any)?.preselectedMemberId;
  const [selectedMemberId, setSelectedMemberId] = useState<string>(preselectedMemberId || "");
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const chamberIdToUse = useGetGraphqlChamberIdVariableObjForRootAdmin();

  // Filter members - show only non-activated members for activation
  const filteredMembers = useMemo(() => {
    if (!allChamberMembers) return [];

    return allChamberMembers.filter((member: any) => {
      return !member.statusForTable || member.statusForTable === enumStrings.manageMemberProfile.notActivatedStatus;
    }).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [allChamberMembers, enumStrings.manageMemberProfile.notActivatedStatus]);

  // Update End Date when Start Date changes
  const handleStartDateChange = (newValue: dayjs.Dayjs | null) => {
    setStartDate(newValue);
    if (newValue) {
      setEndDate(newValue.add(1, "year"));
    } else {
      setEndDate(null);
    }
  };

  const annualFee =
    chamberInfo?.chamberEngageSettings
      ?.memberProfileAndPerkBannerAnnualBannerFeeInDollars || 0;
  const activationFee =
    chamberInfo?.chamberEngageSettings
      ?.memberProfileAndPerkBannerActivationFeeInDollars || 0;
  const chamberShare = Math.max(0, annualFee - activationFee);

  const handleClearAll = () => {
    setSelectedMemberId("");
    setStartDate(null);
    setEndDate(null);
  };

  const [activateMemberProfileAndPerksBannersForChamberMember] =
    useActivateMemberProfileAndPerksBannersForChamberMemberMutation();

  const handleActivate = async () => {
    if (!selectedMemberId || !startDate) {
      setErrorMessage(enumStrings.manageMemberProfile.pleaseSelectMemberAndDate);
      setShowErrorDialog(true);
      return;
    }

    setIsActivating(true);
    try {
      await activateMemberProfileAndPerksBannersForChamberMember({
        variables: {
          ...chamberIdToUse,
          chamberMemberId: selectedMemberId,
          startDateUtcMilli: startDate.startOf("day").valueOf(),
        },
      });

      setShowSuccessDialog(true);
      handleClearAll();
      refetch();
    } catch (error) {
      console.error("Activation failed:", error);
      setErrorMessage(enumStrings.manageMemberProfile.failedToActivate);
      setShowErrorDialog(true);
    } finally {
      setIsActivating(false);
    }
  };

  const selectedMemberName = selectedMemberId
    ? filteredMembers.find(m => m.id === selectedMemberId)?.name || "{Member Company Name}"
    : "{Member Company Name}";

  const infoTexts = [
    enumStrings.manageMemberProfile.activateInfoText1,
    enumStrings.manageMemberProfile.activateInfoText2,
  ];

  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: isMobile ? "16px" : "40px", width: "100%", boxSizing: "border-box" }}>
      <div
        style={{
          width: "1250px",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFFFFF",
            borderRadius: isMobile ? 0 : "20px",
            padding: isMobile ? "14px" : "clamp(16px, 4vw, 45px)",
          }}
        >
          <div
            style={{
              border: isMobile ? "none" : "1px solid #9E9E9E",
              borderRadius: isMobile ? 0 : "16px",
              padding: isMobile ? "8px 0" : "clamp(16px, 4vw, 45px)",
              width: "100%",
              maxWidth: "1160px",
              margin: "0 auto",
              fontFamily: "Inter, sans-serif",
              boxSizing: "border-box",
            }}
          >
            {/* Form Fields Container */}
            <div style={{ maxWidth: "700px", margin: "0 auto" }}>
              {/* Member Dropdown */}
              <div style={{ marginBottom: "20px" }}>
                <FormLabel
                  label={enumStrings.manageChamberEngage.activationForm.chooseMember}
                  required={true}
                  showInfoIcon={false}
                />
                <p style={{ ...activationFormStyles.descriptionText, ...(isMobile ? { fontSize: "13px", lineHeight: "20px", marginBottom: "10px" } : {}) }}>
                  {enumStrings.manageChamberEngage.activationForm.selectMemberDescription}{" "}
                  <strong>{enumStrings.manageChamberEngage.activationForm.selectMemberDescriptionBold}</strong>
                </p>
                <FormControl fullWidth variant="outlined">
                  <Select
                    value={selectedMemberId}
                    onChange={(e) => setSelectedMemberId(e.target.value as string)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Select Member Company Name' }}
                    style={{
                      ...activationFormStyles.inputField,
                      border: "1px solid #000000",
                      borderRadius: "8px",
                    }}
                    renderValue={(value) => {
                      if (isLoadingMembers && preselectedMemberId && value === preselectedMemberId) {
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <CircularProgress size={20} style={{ color: theme.palette.primary.main }} />
                            <span style={{ color: theme.palette.text.secondary }}>{enumStrings.pleaseWait}</span>
                          </div>
                        );
                      }
                      if (!value) {
                        return enumStrings.manageChamberEngage.activationForm.selectMemberCompanyName;
                      }
                      const member = filteredMembers.find((m: any) => m.id === value);
                      return member?.name || enumStrings.manageChamberEngage.activationForm.selectMemberCompanyName;
                    }}
                  >
                    <MenuItem value="" disabled>
                      {enumStrings.manageChamberEngage.activationForm.selectMemberCompanyName}
                    </MenuItem>
                    {filteredMembers.map((member: any) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.name}
                      </MenuItem>
                    ))}
                    {filteredMembers.length === 0 && (
                      <MenuItem disabled value="">
                        {enumStrings.manageChamberEngage.activationForm.noInactiveMembersAvailable}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>

              {/* Start Date */}
              <div style={{ marginBottom: "20px" }}>
                <FormLabel
                  label={enumStrings.manageChamberEngage.activationForm.chooseStartDate}
                  required={false}
                  showInfoIcon={false}
                />
                <p style={{ ...activationFormStyles.descriptionText, ...(isMobile ? { fontSize: "13px", lineHeight: "20px", marginBottom: "10px" } : {}) }}>
                  {enumStrings.manageChamberEngage.activationForm.startDateDescription}
                </p>
                <MuiThemeProvider theme={goldDatePickerTheme}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MobileDatePicker
                      value={startDate}
                      onChange={handleStartDateChange}
                      format="MMM DD, YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          placeholder: enumStrings.manageChamberEngage.activationForm.chooseStartDate,
                          InputProps: {
                            style: activationFormStyles.inputField,
                            startAdornment: (
                              <EventIcon style={{ color: "#000000", marginRight: "8px", fontSize: "20px" }} />
                            ),
                          },
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#000000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                          },
                        },
                        dialog: {
                          sx: {
                            "& .MuiPickersDay-root.Mui-selected": {
                              backgroundColor: `${theme.palette.primary.main} !important`,
                            },
                            "& .MuiPickersDay-root:hover": {
                              backgroundColor: "rgba(225, 167, 49, 0.1)",
                            },
                            "& .MuiPickersDay-today": {
                              borderColor: `${theme.palette.primary.main} !important`,
                            },
                            "& .MuiButton-textPrimary": {
                              color: `${theme.palette.primary.main} !important`,
                            },
                            "& .MuiPickersYear-yearButton.Mui-selected": {
                              backgroundColor: `${theme.palette.primary.main} !important`,
                            },
                            "& .MuiPickersMonth-monthButton.Mui-selected": {
                              backgroundColor: `${theme.palette.primary.main} !important`,
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </MuiThemeProvider>
              </div>

              {/* End Date */}
              <div style={{ marginBottom: "30px" }}>
                <FormLabel
                  label={`${enumStrings.manageChamberEngage.expiryDate} ${enumStrings.manageChamberEngage.activationForm.endDateAutoCalculated}`}
                  required={false}
                  showInfoIcon={false}
                />
                <p style={{ ...activationFormStyles.descriptionText, ...(isMobile ? { fontSize: "13px", lineHeight: "20px", marginBottom: "10px" } : {}) }}>
                  {enumStrings.manageChamberEngage.activationForm.endDateDescription}
                </p>
                <MuiThemeProvider theme={goldDatePickerTheme}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <MobileDatePicker
                      value={endDate}
                      readOnly
                      disabled
                      format="MMM DD, YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: "outlined",
                          placeholder: enumStrings.manageChamberEngage.activationForm.endDateAutoCalculated,
                          InputProps: {
                            style: {
                              ...activationFormStyles.inputField,
                              backgroundColor: "#f5f5f5",
                            },
                            startAdornment: (
                              <EventIcon style={{ color: "#000000", marginRight: "8px", fontSize: "20px" }} />
                            ),
                          },
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#000000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </MuiThemeProvider>
              </div>
            </div>

            {/* Info Text - inside dashed box, below form fields */}
            <div style={{ marginTop: "10px", maxWidth: "700px", margin: "10px auto 0 auto" }}>
              <InfoTextSection texts={infoTexts} />
            </div>

            {/* Annual Fee Section - inside the dashed box */}
            <div style={{ marginTop: "30px", marginBottom: "30px" }}>
              <AnnualFeeDisplay amount={annualFee} label={enumStrings.manageMemberProfile.annualFeaturedMemberFee} />
            </div>

            {/* Fee Table - inside the dashed box */}
            <div style={{ marginBottom: "30px", maxWidth: "700px", margin: "0 auto 30px auto" }}>
              <table style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                overflow: "hidden",
                borderRadius: "8px"
              }}>
                <thead>
                  <tr>
                    <th style={{
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      padding: isMobile ? "10px 8px" : "16px 20px",
                      textAlign: "center",
                      fontSize: isMobile ? "14px" : "22px",
                      fontWeight: 700,
                      lineHeight: isMobile ? "18px" : undefined,
                      borderTopLeftRadius: "8px",
                      border: "none"
                    }}>
                      {enumStrings.manageChamberEngage.nonDuesRevenue}
                    </th>
                    <th style={{
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      padding: isMobile ? "10px 8px" : "16px 20px",
                      textAlign: "center",
                      fontSize: isMobile ? "14px" : "22px",
                      fontWeight: 700,
                      lineHeight: isMobile ? "18px" : undefined,
                      borderTopRightRadius: "8px",
                      border: "none"
                    }}>
                      {enumStrings.manageChamberEngage.activationFee}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{
                      backgroundColor: "#FFF",
                      border: "1px solid #E0E0E0",
                      borderTop: "none",
                      padding: isMobile ? "12px 8px" : "16px 20px",
                      fontSize: isMobile ? "18px" : "32px",
                      fontWeight: 500,
                      color: "#333",
                      textAlign: "center"
                    }}>
                      ${chamberShare}
                    </td>
                    <td style={{
                      backgroundColor: "#FFF",
                      border: "1px solid #E0E0E0",
                      borderTop: "none",
                      padding: isMobile ? "12px 8px" : "16px 20px",
                      fontSize: isMobile ? "18px" : "32px",
                      fontWeight: 500,
                      color: "#333",
                      textAlign: "center"
                    }}>
                      ${activationFee}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Invoice Note - inside the dashed box */}
            <p
              style={{

                fontSize: isMobile ? "14px" : "18px",
                lineHeight: isMobile ? "20px" : "28px",
                fontWeight: 400,
                color: "#000000",
                textAlign: "left",
                maxWidth: "700px",
                margin: isMobile ? "0 auto 20px auto" : "0 auto 30px auto",
              }}
            >
              {enumStrings.manageMemberProfile.invoicingDescription
                .replace("{{memberName}}", selectedMemberName)
                .replace("{{annualFee}}", String(annualFee))}
              <br />
              <span style={{ fontWeight: 700 }}>
                {enumStrings.manageMemberProfile.invoicingDescriptionBold
                  .replace("{{activationFee}}", String(activationFee))}
              </span>
            </p>

            {/* Activate Button - inside the dashed box */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={handleActivate}
                disabled={isActivating}
                style={{
  
                  backgroundColor: theme.palette.success.main,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  borderRadius: "12px",
                  padding: "15px 40px",
                  textTransform: "none" as const,
                  fontSize: "20px",
                  minWidth: "200px",
                  height: "50px",
                  boxShadow: "none",
                  letterSpacing: "-0.01em",
                  lineHeight: "150%",
                }}
              >
                {isActivating ? <CircularProgress size={24} color="inherit" /> : enumStrings.manageMemberProfile.activateButton}
              </Button>
            </div>

            {/* Success Dialog */}
            <SuccessDialog
              open={showSuccessDialog}
              onClose={() => setShowSuccessDialog(false)}
              message={enumStrings.manageMemberProfile.featuredMemberActivatedSuccess}
              autoDismissMs={15000}
            />
            <SuccessDialog
              open={showErrorDialog}
              onClose={() => setShowErrorDialog(false)}
              message={errorMessage}
              variant="error"
              autoDismissMs={5000}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Revenue Table Component
interface RevenueTableProps {
  chamberInfo:
  | GetUser_RootAdminChamberAdminQuery["getUser"]["chamberInfo"]
  | undefined;
}

const RevenueTable: React.FC<RevenueTableProps> = ({ chamberInfo }) => {
  const enumStrings = useEnumStrings();

  const nonDuesRevenue = useMemo(() => {
    const annualFee = Number(
      chamberInfo?.chamberEngageSettings
        ?.memberProfileAndPerkBannerAnnualBannerFeeInDollars || 0
    );
    const activationFee = Number(
      chamberInfo?.chamberEngageSettings
        ?.memberProfileAndPerkBannerActivationFeeInDollars || 0
    );
    const nonDuesRevenue = annualFee - activationFee;
    return `$${nonDuesRevenue.toFixed(0)}`;
  }, [chamberInfo]);

  const commonPadding = "8px";

  return (
    <table
      style={{
        width: "100%",
        maxWidth: "400px",
        borderCollapse: "collapse",
        border: "1px solid #d0d0d0",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "white",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <thead>
        <tr>
          <th
            style={{
              padding: commonPadding,
              color: "#333",
              fontWeight: "bold",
              borderBottom: "1px solid #d0d0d0",
              borderRight: "1px solid #d0d0d0",
              fontSize: "18px",
              backgroundColor: "white",
            }}
          >
            {enumStrings.manageChamberEngage.nonDuesRevenue}
          </th>
          <th
            style={{
              padding: commonPadding,
              color: "#333",
              fontWeight: "bold",
              borderBottom: "1px solid #d0d0d0",
              fontSize: "18px",
              position: "relative",
              backgroundColor: "white",
            }}
          >
            {enumStrings.manageChamberEngage.activationFee}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td
            style={{
              padding: commonPadding,
              borderRight: "1px solid #d0d0d0",
              fontSize: "18px",
              backgroundColor: "white",
            }}
          >
            {nonDuesRevenue}
          </td>
          <td
            style={{
              padding: commonPadding,
              fontSize: "18px",
              backgroundColor: "white",
            }}
          >
            $
            {chamberInfo?.chamberEngageSettings
              ?.memberProfileAndPerkBannerActivationFeeInDollars || 0}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const useStyles = makeStyles((themes: any) => ({
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "200px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
      minHeight: "auto",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
  },
  title: {
    fontSize: "32px",
    color: "white",
    fontWeight: "bold",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
    },
  },
  headerIcons: {
    display: "flex",
    gap: "15px",
    alignItems: "center",
  },
  headerIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  description: {
    fontSize: "18px",
    color: "white",
    marginBottom: "20px",
    lineHeight: "1.5",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column" as const,
      alignItems: "stretch",
      gap: "8px",
      width: "100%",
      "& > div": { width: "100%" },
      "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  videoButton: {
    backgroundColor: "white",
    color: "#D4A353",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 20px",
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    backgroundColor: theme.palette.primary.main,
    [theme.breakpoints.up("lg")]: {
      paddingTop: "30px",
    },
  },
  contentContainer: {
    backgroundColor: theme.palette.primary.main,
    padding: "0 40px 40px 40px",
    overflowX: "auto",
    [theme.breakpoints.down("sm")]: {
      padding: "0 0 20px 0",
    },
  },
  content: {
    display: "flex",
    background: "#FFFFFF",
    minHeight: "70vh",
    borderRadius: "16px",
    padding: "30px",
    flexDirection: "column",
    overflowX: "auto",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
      borderRadius: 0,
    },
  },
  searchContainer: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      gap: "10px",
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchBar: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "404px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    height: "45px",
    "& .MuiOutlinedInput-root": {
      height: "45px",
      borderRadius: "8px",

      fontSize: "14px",
      fontWeight: 500,
      padding: "8px 9px",
      "& fieldset": {
        borderColor: "#000000",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#000000",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000000 !important",
      },
    },
    "& .MuiOutlinedInput-input": {

      fontSize: "14px",
      fontWeight: 500,
      color: "#000",
      padding: "0",
      "&::placeholder": {
        color: "#9E9E9E",
        opacity: 1,
      },
    },
  },
  filterSelect: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "auto",
    minWidth: "180px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: "unset",
    },
    "&:focus, &:focus-visible": {
      outline: "none",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        borderColor: "#000000",
      },
      "&:hover fieldset": {
        borderColor: "#000000",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000000 !important",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#000000 !important",
      },
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#000000 !important",
    },
    "& .MuiSelect-select": {
      paddingTop: "12px",
      paddingBottom: "12px",
      paddingRight: "32px",
      color: "#000",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    "& .MuiSelect-icon": {
      display: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      borderColor: "#000000 !important",
    }
  },
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: "1031px",
    height: "auto",
    maxHeight: "90vh",
    backgroundColor: "#E6E6E6",
    borderRadius: "20px",
    position: "relative",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
    maxWidth: "95%",
    padding: "30px",
    paddingBottom: "40px",
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "calc(100% - 32px)",
      margin: "0 16px",
      padding: "16px",
      paddingBottom: "24px",
      borderRadius: "12px",
    },
  },
  closeButton: {
    position: "absolute",
    width: "30px",
    height: "30px",
    right: "16px",
    top: "16px",
    backgroundColor: (theme.palette as any).delete.main,
    borderRadius: "100px",
    color: "#FFFFFF",
    padding: "0",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "#d01820",
    },
  },
  modalTitle: {
    width: "697px",
    maxWidth: "100%",
    position: "relative",

    fontWeight: 600,
    fontSize: "25px",
    lineHeight: "110%",
    textAlign: "center",
    color: "#000000",
    margin: "0 auto 24px auto",
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
      width: "100%",
    },
  },
  modalContentBox: {
    boxSizing: "border-box",
    width: "100%",
    background: "#FFFFFF",
    border: "1px solid #000000",
    borderRadius: "16px",
    padding: "30px",
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
      borderRadius: "12px",
      marginBottom: "16px",
    },
  },
  modalBodyText: {

    fontWeight: 400,
    fontSize: "20px",
    lineHeight: "200%",
    color: "#000000",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      lineHeight: "1.55",
    },
  },
  modalButtonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "0px",
    gap: "40px",
    justifyContent: "center",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      gap: "16px",
      flexDirection: "column",
    },
  },
  yesButton: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "15px 96px",
    gap: "10px",
    width: "290px",
    height: "54px",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "12px",

    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "150%",
    letterSpacing: "-0.04em",
    color: "#FFFFFF",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "#d4a353",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: "12px 20px",
      fontSize: "16px",
    },
  },
  okButton: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "15px 96px",
    gap: "10px",
    width: "290px",
    height: "54px",
    backgroundColor: (theme.palette as any).delete.main,
    borderRadius: "12px",

    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "150%",
    letterSpacing: "-0.04em",
    color: "#FFFFFF",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "#d01820",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: "12px 20px",
      fontSize: "16px",
    },
  },
  filledDatePicker: {
    backgroundColor: "#F2F2F6",
    borderRadius: "15px",
    height: "55px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      width: "330px",
    },
    [theme.breakpoints.up("sm")]: {
      width: "360px",
    },
    "& .MuiInputBase-root": {
      backgroundColor: "white !important",
      borderRadius: "15px !important",
    },
    "& .MuiInputLabel-filled.MuiInputLabel-shrink": {
      color: "#37474f",
    },
    "& .MuiInputBase-input.MuiFilledInput-input.Mui-disabled": {
      color: "black",
      opacity: 1,
      "-webkit-text-fill-color": "black",
    },
  },
  desktopTableWrapper: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileCardList: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "16px",
    },
  },
  memberCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    border: "1px solid #E8E8E8",
    padding: "16px",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "14px",
  },
  cardAvatar: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold" as const,
    fontSize: "16px",
    flexShrink: 0,
    letterSpacing: "0.5px",
  },
  cardCompanyName: {
    fontWeight: 700,
    fontSize: "17px",
    color: "#1A1A1A",
    lineHeight: 1.2,
    marginBottom: "4px",
  },
  cardStatus: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  cardDivider: {
    borderTop: "1px solid #E8E8E8",
    marginBottom: "12px",
  },
  cardInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  cardInfoLabel: {
    color: "#666666",
    fontSize: "14px",
  },
  cardInfoValue: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#1A1A1A",
    textAlign: "right" as const,
  },
  cardAwaitingText: {
    color: "#888888",
    fontStyle: "italic" as const,
    fontSize: "14px",
    margin: "4px 0 8px 0",
  },
  cardButtonsRow: {
    display: "flex",
    gap: "10px",
    marginTop: "16px",
  },
  cardButton: {
    flex: 1,
    borderRadius: "10px",
    padding: "12px 8px",
    fontWeight: "bold" as const,
    fontSize: "14px",
    textTransform: "none" as const,
    boxShadow: "none",
  },
}));

type CustomActiveMemberProfileAndPerksBannersSettingsReturnForTable = {
  activationInfo: Omit<
    ActiveMemberProfileAndPerksBannersSettingsActivationInfoReturn,
    | "sentEmailNotificationOfUpcomingExpiry"
    | "chamberMemberHasAcceptedAgreement"
  >;
  reactivationInfo?:
  | Omit<
    ActiveMemberProfileAndPerksBannersSettingsActivationInfoReturn,
    | "sentEmailNotificationOfUpcomingExpiry"
    | "chamberMemberHasAcceptedAgreement"
  >
  | undefined
  | null;
};

type Goner = {
  activeMemberProfileAndPerksBannersSettings?:
  | CustomActiveMemberProfileAndPerksBannersSettingsReturnForTable
  | undefined
  | null;
};

type Bongus = Omit<
  ChamberMemberReturn,
  "activeMemberProfileAndPerksBannersSettings"
> &
  Goner & {
    statusForTable: string;
    annualFeeForTable: number | undefined | null;
    startDateForTable: number | undefined | null;
    expiryDateForTable: number | undefined | null;
  };

export type AllTableAllChamberMembersForChamberDataType = Bongus[];


const useGetChamberIdToUseToPullAllChamberMembersForRootOrChamberAdmin = () => {
  const {
    state: { userType },
  } = useContext(UserTypeContext);
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const location = useLocation();
  const chamberIdToUse = useMemo(() => {
    if (userType === UserType.RootAdmin) {
      return (
        (location.state || { chamberId: undefined }) as {
          chamberId: string | undefined;
        }
      ).chamberId;
    }
    return chamberInfo?.id;
  }, [chamberInfo?.id, location.state, userType]);

  return chamberIdToUse;
};

function FullPageManageAllChamberMembersForChamberTable({
  allChamberMembersForChamberData,
}: {
  allChamberMembersForChamberData: AllTableAllChamberMembersForChamberDataType;
}) {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();

  const [searchInput, setSearchInput] = React.useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const handleChange = (e: any) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  };

  // Filter data (CommonTable will handle sorting)
  const filteredData: AllTableAllChamberMembersForChamberDataType =
    React.useMemo(() => {
      if (!allChamberMembersForChamberData) {
        return [];
      }

      let filtered = allChamberMembersForChamberData.slice();

      // Apply search filter
      if (searchInput.length > 0) {
        filtered = filtered.filter((i) => {
          return i.name.toLowerCase().includes(searchInput.toLowerCase());
        });
      }

      // Apply category filter
      if (categoryFilter !== "all") {
        filtered = filtered.filter(member => (member.categoryId as any)?.includes(categoryFilter));
      }

      // Apply status filter
      if (statusFilter !== "all") {
        if (statusFilter === "active") {
          filtered = filtered.filter(member =>
            member.activeMemberProfileAndPerksBannersSettings?.activationInfo
          );
        } else if (statusFilter === "inactive") {
          filtered = filtered.filter(member =>
            !member.activeMemberProfileAndPerksBannersSettings?.activationInfo
          );
        }
      }

      return filtered;
    }, [allChamberMembersForChamberData, searchInput, categoryFilter, statusFilter]);

  const [dbProcessInProgress, setDbProcessInProgress] = useState(false);
  const [
    chamberMemberToShowCaseInAnyOpenMmodal,
    setChamberMemberToShowCaseInAnyOpenMmodal,
  ] = useState<Bongus | null>(null);

  const [dialogState, setDialogState] = useState<{ open: boolean; message: string; variant: "success" | "error" }>({ open: false, message: "", variant: "success" });

  const [openActivateForMemberModal, setOpenActivateForMemberModal] =
    useState(false);
  const handleOpenActivateForMemberModal = useCallback(
    ({ chamberMember }: { chamberMember: Bongus }) => {
      setChamberMemberToShowCaseInAnyOpenMmodal(chamberMember);
      setOpenActivateForMemberModal(true);
    },
    []
  );

  const [openDeactivateForMemberModal, setOpenDeactivateForMemberModal] =
    useState(false);
  const handleOpenDeactivateForMemberModal = useCallback(
    ({ chamberMember }: { chamberMember: Bongus }) => {
      setChamberMemberToShowCaseInAnyOpenMmodal(chamberMember);
      setOpenDeactivateForMemberModal(true);
    },
    []
  );

  const [openReactivateForMemberModal, setOpenReactivateForMemberModal] =
    useState<"newRe" | false>(false);
  const handleOpenReactivateForMemberModal = useCallback(
    ({
      chamberMember,
      modalType,
    }: {
      chamberMember: Bongus;
      modalType: "newRe";
    }) => {
      setChamberMemberToShowCaseInAnyOpenMmodal(chamberMember);
      setAdvertisingStartDateChange(
        chamberMember.activeMemberProfileAndPerksBannersSettings?.activationInfo
          ?.startDateUtcMilli || 0
      );
      setOpenReactivateForMemberModal(modalType);
    },
    []
  );

  const [showAdvertisingStartDateError, setShowAdvertisingStartDateError] =
    useState(false);
  const [advertisingStartDateChange, setAdvertisingStartDateChange] =
    useState(0);
  const handleAdvdertisingStartDateChange = useCallback((val) => {
    setShowAdvertisingStartDateError(false);
    setAdvertisingStartDateChange(dayjs(val).startOf("day").valueOf());
  }, []);

  const handleCloseActivateForMemberModal = useCallback(() => {
    setAdvertisingStartDateChange(0);
    setShowAdvertisingStartDateError(false);
    setOpenActivateForMemberModal(false);
  }, []);

  const handleCloseReactivateForMemberModal = useCallback(() => {
    setAdvertisingStartDateChange(0);
    setShowAdvertisingStartDateError(false);
    setOpenReactivateForMemberModal(false);
  }, []);

  const handleCloseDeactivateForMemberModal = useCallback(() => {
    setOpenDeactivateForMemberModal(false);
  }, []);

  const graphqlChamberIdVariableObjForRootAdmin =
    useGetGraphqlChamberIdVariableObjForRootAdmin();
  const chamberIdToUse =
    useGetChamberIdToUseToPullAllChamberMembersForRootOrChamberAdmin();

  const [activateMemberProfileAndPerksBannersForChamberMember] =
    useActivateMemberProfileAndPerksBannersForChamberMemberMutation();
  const handleActivateForMember = useCallback(async () => {
    setDbProcessInProgress(true);

    //validate data
    if (!(advertisingStartDateChange > 0)) {
      if (advertisingStartDateChange <= 0) {
        setShowAdvertisingStartDateError(true);
      }

      setDbProcessInProgress(false);
      return;
    }

    await activateMemberProfileAndPerksBannersForChamberMember({
      variables: {
        ...graphqlChamberIdVariableObjForRootAdmin,
        chamberMemberId: chamberMemberToShowCaseInAnyOpenMmodal?.id || "",
        startDateUtcMilli: advertisingStartDateChange,
      },
      refetchQueries: [
        {
          query: GetAllChamberMembers_RootAndChamberAdminOnlyDocument,
          variables: {
            chamberId: chamberIdToUse,
          },
        },
      ],
    });

    setDialogState({ open: true, message: enumStrings.activatedSuccessfully, variant: "success" });
    handleCloseActivateForMemberModal();
    setDbProcessInProgress(false);
  }, [
    activateMemberProfileAndPerksBannersForChamberMember,
    advertisingStartDateChange,
    chamberIdToUse,
    chamberMemberToShowCaseInAnyOpenMmodal?.id,
    enumStrings.activatedSuccessfully,
    graphqlChamberIdVariableObjForRootAdmin,
    handleCloseActivateForMemberModal,
  ]);

  const [reactivateMemberProfileAndPerksBannersForChamberMember] =
    useReactivateMemberProfileAndPerksBannersForChamberMemberMutation({ errorPolicy: "all" });
  const handleReactivateForMember = useCallback(async () => {
    setDbProcessInProgress(true);

    const result = await reactivateMemberProfileAndPerksBannersForChamberMember({
      variables: {
        ...graphqlChamberIdVariableObjForRootAdmin,
        chamberMemberId: chamberMemberToShowCaseInAnyOpenMmodal?.id || "",
      },
      refetchQueries: [
        {
          query: GetAllChamberMembers_RootAndChamberAdminOnlyDocument,
          variables: {
            chamberId: chamberIdToUse,
          },
        },
      ],
    });

    if (result.errors && result.errors.length > 0) {
      console.error("Reactivation failed:", result.errors);
      setDialogState({ open: true, message: enumStrings.manageMemberProfile.alreadyReactivated, variant: "error" });
    } else {
      setDialogState({ open: true, message: enumStrings.manageMemberProfile.reActivatedSuccessfully, variant: "success" });
    }
    handleCloseReactivateForMemberModal();
    setDbProcessInProgress(false);
  }, [
    chamberIdToUse,
    chamberMemberToShowCaseInAnyOpenMmodal?.id,
    enumStrings.manageMemberProfile.reActivatedSuccessfully,
    enumStrings.manageMemberProfile.alreadyReactivated,
    graphqlChamberIdVariableObjForRootAdmin,
    handleCloseReactivateForMemberModal,
    reactivateMemberProfileAndPerksBannersForChamberMember,
  ]);

  const [deactivateMemberProfileAndPerksBannersForChamberMember] =
    useDeactivateMemberProfileAndPerksBannersForChamberMemberMutation();
  const handleDeactivateForMember = useCallback(async () => {
    setDbProcessInProgress(true);

    await deactivateMemberProfileAndPerksBannersForChamberMember({
      variables: {
        ...graphqlChamberIdVariableObjForRootAdmin,
        chamberMemberId: chamberMemberToShowCaseInAnyOpenMmodal?.id || "",
      },
      refetchQueries: [
        {
          query: GetAllChamberMembers_RootAndChamberAdminOnlyDocument,
          variables: {
            chamberId: chamberIdToUse,
          },
        },
      ],
    });

    setDialogState({ open: true, message: enumStrings.deactivatedSuccessfully, variant: "success" });
    handleCloseDeactivateForMemberModal();
    setDbProcessInProgress(false);
  }, [
    chamberIdToUse,
    chamberMemberToShowCaseInAnyOpenMmodal?.id,
    deactivateMemberProfileAndPerksBannersForChamberMember,
    enumStrings.deactivatedSuccessfully,
    graphqlChamberIdVariableObjForRootAdmin,
    handleCloseDeactivateForMemberModal,
  ]);

  const chamberInfo = useGetChamberFromUserIdInAuth() as
    | GetUser_RootAdminChamberAdminQuery["getUser"]["chamberInfo"]
    | undefined;

  const { categoryIdToHumanReadableName } = useGetCategoryIdToHumanReadableName();

  // Get unique categories from members
  const categories = useMemo(() => {
    const catSet = new Set<string>();
    allChamberMembersForChamberData.forEach(member => {
      if (member.categoryId && member.categoryId.length > 0) {
        member.categoryId.forEach((cat: string) => {
          if (cat) catSet.add(cat);
        });
      }
    });
    return Array.from(catSet);
  }, [allChamberMembersForChamberData]);

  const columns: HeadCell[] = [
    {
      id: "name",
      label: enumStrings.manageMemberProfile.name,
      description: enumStrings.manageMemberProfile.nameDescription,
    },
    {
      id: "statusForTable",
      label: "Activation Status",
      description: "Feature Member",
    },
    {
      id: "configButtons",
      label: enumStrings.manageMemberProfile.actionLabel,
      description: "Activate, Reactivate or Deactivate",
      sortable: false,
    },
    {
      id: "annualFeeForTable",
      label: "Feature Member Banner",
      description: "Annual Cost",
    },
    {
      id: "startDateForTable",
      label: enumStrings.manageMemberProfile.startDateForTable,
      description: enumStrings.manageMemberProfile.startDateDescription,
    },
    {
      id: "expiryDateForTable",
      label: "End Date",
      description: enumStrings.manageMemberProfile.expiryDateDescription,
    },
  ];

  // Custom cell renderer for the table
  const renderCell = useCallback(
    (column: HeadCell, chamberMember: Bongus) => {
      const isActive = !!chamberMember.activeMemberProfileAndPerksBannersSettings?.activationInfo;

      switch (column.id) {
        case "name":
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {chamberMember.logo ? (
                <img
                  src={chamberMember.logo}
                  alt={chamberMember.name}
                  style={{
                    width: "70px",
                    height: "59px",
                    borderRadius: "8px",
                    objectFit: "contain"
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "70px",
                    height: "59px",
                    borderRadius: "8px",
                    backgroundColor: "#9E9E9E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "24px",
                    flexShrink: 0,
                  }}
                >
                  {(chamberMember.name || "").charAt(0).toUpperCase()}
                </div>
              )}
              <span>{chamberMember.name}</span>
            </div>
          );
        case "statusForTable": {
          const hasReactivation = !!chamberMember.activeMemberProfileAndPerksBannersSettings?.reactivationInfo;
          if (!isActive) {
            return (
              <span style={{
                color: (theme.palette as any).delete.main,
                fontWeight: 600,
                fontSize: "14px",
              }}>
                {enumStrings.manageMemberProfile.notActivatedStatus}
              </span>
            );
          }
          if (hasReactivation) {
            const reactivationStartDate = chamberMember.activeMemberProfileAndPerksBannersSettings!.reactivationInfo!.startDateUtcMilli;
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "16px",
                }}>
                  {enumStrings.manageMemberProfile.reactivatedStatus}
                </span>
                <span style={{
                  color: "#4CAF50",
                  fontWeight: 600,
                  fontSize: "14px",
                }}>
                  {enumStrings.manageMemberProfile.reactivatedAsOf.replace("{{date}}", getLocalTimestamp(reactivationStartDate, true))}
                </span>
              </div>
            );
          }
          return (
            <span style={{
              color: theme.palette.success.main,
              fontWeight: 600,
              fontSize: "14px",
            }}>
              {enumStrings.manageMemberProfile.activatedStatus}
            </span>
          );
        }
        case "configButtons":
          return isActive ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
              <Button
                variant="contained"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  fontSize: "12px",
                  fontWeight: 500,
                  fontFamily: "Montserrat, sans-serif",
                  borderRadius: "22px",
                  padding: "8px 12px",
                  textTransform: "uppercase",
                  minWidth: "113px",
                  height: 31,
                  boxShadow: "none",
                  lineHeight: "15px",
                  whiteSpace: "nowrap",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenReactivateForMemberModal({
                    chamberMember,
                    modalType: "newRe",
                  });
                }}
              >
                {enumStrings.manageChamberEngage.buttons.reactivate}
              </Button>
              <Button
                variant="contained"
                style={{
                  backgroundColor: "#24272C",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: 500,
                  fontFamily: "Montserrat, sans-serif",
                  borderRadius: "22px",
                  padding: "8px 12px",
                  textTransform: "uppercase",
                  minWidth: "113px",
                  height: 31,
                  boxShadow: "none",
                  lineHeight: "15px",
                  whiteSpace: "nowrap",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeactivateForMemberModal({
                    chamberMember,
                  });
                }}
              >
                {enumStrings.manageChamberEngage.buttons.deactivate}
              </Button>
            </div>
          ) : (
            <Button
              variant="contained"
              style={{
                backgroundColor: theme.palette.primary.main,
                color: "#FFFFFF",
                fontSize: "12px",
                fontWeight: 500,
                fontFamily: "Montserrat, sans-serif",
                borderRadius: "22px",
                padding: "8px 12px",
                textTransform: "uppercase",
                boxShadow: "none",
                minWidth: "113px",
                height: 31,
                lineHeight: "15px",
                whiteSpace: "nowrap",
              }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(RedirectUri.chamberEngageActivateFeaturedMember, {
                  preselectedMemberId: chamberMember.id,
                });
              }}
            >
              {enumStrings.manageMemberProfile.activateButton}
            </Button>
          );
        case "annualFeeForTable":
          return isActive
            ? `$${chamberMember.activeMemberProfileAndPerksBannersSettings?.activationInfo.memberProfileAndPerkBannerAnnualBannerFeeInDollarsAtTimeOfActivation}`
            : "-";
        case "startDateForTable":
          if (isActive && chamberMember.activeMemberProfileAndPerksBannersSettings?.activationInfo) {
            return (
              <span>{getLocalTimestamp(
                chamberMember.activeMemberProfileAndPerksBannersSettings.activationInfo.startDateUtcMilli,
                true
              )}</span>
            );
          }
          return "-";
        case "expiryDateForTable":
          if (isActive && chamberMember.activeMemberProfileAndPerksBannersSettings?.activationInfo) {
            const startDateUtcMilli = chamberMember.activeMemberProfileAndPerksBannersSettings.activationInfo.startDateUtcMilli;
            const expiryDateUtcMilli = getExpiryUtcMilliTimestampThatIsSpecifiedMonthsAfterSpecifiedAdvertisingStartDateUtcMilli({
              advertisingStartDateUtcMilli: startDateUtcMilli,
              months: numberOfMonthsTillExpiry,
            });
            const now = new Date();
            const endDateObj = new Date(expiryDateUtcMilli);

            // Calculate remaining months and days
            let remainingMonths = (endDateObj.getFullYear() - now.getFullYear()) * 12 + (endDateObj.getMonth() - now.getMonth());
            let remainingDays = endDateObj.getDate() - now.getDate();
            if (remainingDays < 0) {
              remainingMonths -= 1;
              const prevMonth = new Date(endDateObj.getFullYear(), endDateObj.getMonth(), 0);
              remainingDays += prevMonth.getDate();
            }
            remainingMonths = Math.max(0, remainingMonths);
            remainingDays = Math.max(0, remainingDays);

            const totalDaysLeft = Math.max(0, Math.ceil((endDateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
            const timeRemainingText = enumStrings.manageMemberProfile.timeRemainingDays
                .replace("{{days}}", String(totalDaysLeft));

            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                <span>{getLocalTimestamp(expiryDateUtcMilli, true)}</span>
                <span style={{ fontSize: "14px", color: totalDaysLeft <= 20 ? "#E53935" : "#4CAF50", fontWeight: 700 }}>
                  {timeRemainingText}
                </span>
              </div>
            );
          }
          return "-";
        default:
          return null;
      }
    },
    [handleOpenActivateForMemberModal, handleOpenDeactivateForMemberModal, handleOpenReactivateForMemberModal, history]
  );

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
      }}
    >
      {openDeactivateForMemberModal && (
        <Modal className={classes.modal} open={openDeactivateForMemberModal}>
          <div className={classes.paper} style={{ fontFamily: "Inter, sans-serif" }}>
            <IconButton
              className={classes.closeButton}
              onClick={handleCloseDeactivateForMemberModal}
            >
              <CloseIcon style={{ fontSize: "16px" }} />
            </IconButton>
            <h2 className={classes.modalTitle}>
              DEACTIVATE
              <br />
              {chamberMemberToShowCaseInAnyOpenMmodal?.name}
              <br />
              Featured Member Profile and Perk Banners
            </h2>
            <div className={classes.modalContentBox} style={{ overflowY: "scroll", maxHeight: "328px" }}>
              <p className={classes.modalBodyText} style={{ fontFamily: "Inter, sans-serif" }}>
                You are about to <strong>DEACTIVATE</strong> the Featured Member status for <strong>{chamberMemberToShowCaseInAnyOpenMmodal?.name}</strong>. All existing image and video banners will be removed from the Chamber Perks App™ immediately.
              </p>
              <p className={classes.modalBodyText} style={{ fontFamily: "Inter, sans-serif", marginTop: "16px" }}>
                <strong>{chamberMemberToShowCaseInAnyOpenMmodal?.name}</strong> will no longer have Featured Member status in their dashboard and will no longer be able to add image or video banners to their profile or perks.
              </p>
              <p className={classes.modalBodyText} style={{ fontFamily: "Inter, sans-serif", marginTop: "24px", fontWeight: 700, textAlign: "center" }}>
                Are you sure you want to deactivate?
              </p>
            </div>
            <div className={classes.modalButtonContainer}>
              <Button
                className={classes.yesButton}
                onClick={handleDeactivateForMember}
                variant="contained"
                disabled={dbProcessInProgress}
                style={{ backgroundColor: "#000000" }}
              >
                {dbProcessInProgress ? (
                  <CircularProgress
                    size={24}
                    style={{ color: "white" }}
                  />
                ) : (
                  "DEACTIVATE"
                )}
              </Button>
              <Button
                className={classes.okButton}
                onClick={handleCloseDeactivateForMemberModal}
                variant="contained"
                disabled={dbProcessInProgress}
              >
                {enumStrings.manageMemberProfile.cancel}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {openReactivateForMemberModal && (
        <Modal
          className={classes.modal}
          open={openReactivateForMemberModal ? true : false}
        >
          <div className={classes.paper}>
            <IconButton
              className={classes.closeButton}
              onClick={handleCloseReactivateForMemberModal}
            >
              <CloseIcon style={{ fontSize: "16px" }} />
            </IconButton>
            <h2 className={classes.modalTitle}>
              {enumStrings.manageMemberProfile.reaActivate.toUpperCase()}
              <br />
              {chamberMemberToShowCaseInAnyOpenMmodal?.name}
              <br />
              Featured Member Profile and Perk Banners
            </h2>
            <div className={classes.modalContentBox} style={{ overflowY: "scroll" }}>
              <p className={classes.modalBodyText}>
                You are about to <strong>REACTIVATE</strong> the Featured Member status for{" "}
                <strong>{chamberMemberToShowCaseInAnyOpenMmodal?.name}</strong>, which will be active until{" "}
                <strong>
                  {advertisingStartDateChange > 0
                    ? getLocalTimestamp(
                        getExpiryUtcMilliTimestampThatIsSpecifiedMonthsAfterSpecifiedAdvertisingStartDateUtcMilli({
                          advertisingStartDateUtcMilli: advertisingStartDateChange,
                          months: numberOfMonthsTillExpiry,
                        }),
                        true
                      )
                    : "{End Date}"}
                </strong>. All existing image and video banners will remain visible in the Chamber Perks App™ until this date unless reactivated again.
              </p>
              <p className={classes.modalBodyText} style={{ marginTop: "16px" }}>
                Your chamber is responsible for invoicing{" "}
                <strong>{chamberMemberToShowCaseInAnyOpenMmodal?.name}</strong>{" "}
                ${chamberInfo?.chamberEngageSettings?.memberProfileAndPerkBannerAnnualBannerFeeInDollars} + tax to reactivate their Featured Member status. Once reactivated, your chamber will be invoiced the annual $50 activation fee.
              </p>
              <p className={classes.modalBodyText} style={{ marginTop: "16px" }}>
                Your chamber will receive an automatic email notification 60 days before the end date so you can reach out to the member to resell the Feature Member banner activation.
              </p>
              <p className={classes.modalBodyText} style={{ marginTop: "24px", fontWeight: 700, textAlign: "center" }}>
                Are you sure you want to reactivate?
              </p>
            </div>
            <div className={classes.modalButtonContainer}>
              <Button
                className={classes.yesButton}
                onClick={() => {
                  handleReactivateForMember();
                }}
                variant="contained"
                disabled={dbProcessInProgress}
              >
                {dbProcessInProgress ? (
                  <CircularProgress
                    size={24}
                    style={{ color: "white" }}
                  />
                ) : (
                  enumStrings.manageMemberProfile.reaActivate
                )}
              </Button>
              <Button
                className={classes.okButton}
                onClick={handleCloseReactivateForMemberModal}
                variant="contained"
                disabled={dbProcessInProgress}
              >
                {enumStrings.manageMemberProfile.cancel.toUpperCase()}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {openActivateForMemberModal && (
        <Modal className={classes.modal} open={openActivateForMemberModal}>
          <div className={classes.paper}>
            <IconButton
              className={classes.closeButton}
              onClick={handleCloseActivateForMemberModal}
            >
              <CloseIcon style={{ fontSize: "16px" }} />
            </IconButton>
            <h2 className={classes.modalTitle}>
              {enumStrings.manageMemberProfile.activate}{" "}
              {chamberMemberToShowCaseInAnyOpenMmodal?.name}'s{" "}
              {enumStrings.manageMemberProfile.enhancedMemberProfile}
            </h2>
            <div className={classes.modalContentBox}>
              <p className={classes.modalBodyText}>
                {enumStrings.manageMemberProfile.enterStartDate}
              </p>
              <p className={classes.modalBodyText} style={{ marginTop: "8px" }}>
                {enumStrings.manageMemberProfile.emailNotification}
              </p>
              <p className={classes.modalBodyText} style={{ marginTop: "8px" }}>
                {enumStrings.manageMemberProfile.bannersDeactivation}
                {advertisingStartDateChange > 0 ? (
                  <span style={{ fontWeight: "bold" }}>
                    {" "}
                    (
                    {getLocalTimestamp(
                      getExpiryUtcMilliTimestampThatIsSpecifiedMonthsAfterSpecifiedAdvertisingStartDateUtcMilli(
                        {
                          advertisingStartDateUtcMilli:
                            advertisingStartDateChange,
                          months: numberOfMonthsTillExpiry,
                        }
                      ),
                      true
                    )}
                    )
                  </span>
                ) : (
                  ""
                )}
              </p>
              <p className={classes.modalBodyText} style={{ marginTop: "8px" }}>
                {enumStrings.manageMemberProfile.reactivateBanners}
              </p>

              <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <LocalizationProvider
                  key={`advertisingStartDate_${chamberMemberToShowCaseInAnyOpenMmodal?.id}`}
                  dateAdapter={AdapterDayjs}
                >
                  <MobileDatePicker
                    disabled={dbProcessInProgress}
                    slotProps={{
                      textField: {
                        InputProps: {
                          disableUnderline: true,
                        },
                        className: classes.filledDatePicker,
                        variant: "filled",
                        sx: {
                          "& .MuiFilledInput-root:before": {
                            borderBottom: "none",
                          },
                          "& .MuiFilledInput-root:after": {
                            borderBottom: "none",
                          },
                          "& .MuiFilledInput-root:hover:before": {
                            borderBottom: "none",
                          },
                          "& .MuiInputBase-input.MuiFilledInput-input.Mui-readOnly.MuiInputBase-readOnly":
                          {
                            cursor: "pointer",
                          },
                        },
                      },
                    }}
                    minDate={dayjs(Date.now())}
                    label={enumStrings.manageMemberProfile.startDateForTable}
                    format="ll"
                    onChange={handleAdvdertisingStartDateChange}
                    value={
                      advertisingStartDateChange > 0
                        ? dayjs(advertisingStartDateChange)
                        : undefined
                    }
                  />
                </LocalizationProvider>
                {showAdvertisingStartDateError && (
                  <p style={{
                    margin: "8px 0 0 0",
                    color: "red",
                    fontSize: "14px",
                  }}>
                    {enumStrings.manageMemberProfile.selectToActivate}
                  </p>
                )}
              </div>

              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <h3 style={{
  
                  fontWeight: 600,
                  fontSize: "20px",
                  margin: "0 0 12px 0",
                  color: "#000"
                }}>
                  {enumStrings.manageMemberProfile.AnnualFee}
                </h3>
                <Chip
                  label={
                    <span style={{
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}>
                      $
                      {
                        chamberInfo?.chamberEngageSettings
                          ?.memberProfileAndPerkBannerAnnualBannerFeeInDollars
                      }
                    </span>
                  }
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    padding: "8px 16px",
                    height: "auto"
                  }}
                />
              </div>

              <div style={{ marginTop: "24px" }}>
                <RevenueTable chamberInfo={chamberInfo} />
              </div>
            </div>
            <div className={classes.modalButtonContainer}>
              <Button
                className={classes.yesButton}
                onClick={handleActivateForMember}
                variant="contained"
                disabled={dbProcessInProgress}
              >
                {dbProcessInProgress ? (
                  <CircularProgress
                    size={24}
                    style={{ color: "white" }}
                  />
                ) : (
                  enumStrings.manageMemberProfile.activate.toUpperCase()
                )}
              </Button>
              <Button
                className={classes.okButton}
                onClick={handleCloseActivateForMemberModal}
                variant="contained"
                disabled={dbProcessInProgress}
              >
                {enumStrings.manageMemberProfile.cancel.toUpperCase()}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <div className={classes.searchContainer}>
        <TextField
          className={classes.searchBar}
          placeholder={enumStrings.filters.searchByMemberName}
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={handleChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#999" }} />
              </InputAdornment>
            ),
          }}
        />

        <Select
          className={classes.filterSelect}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as string)}
          variant="outlined"
          displayEmpty
          style={{ color: categoryFilter === "all" ? "#9E9E9E" : "#000" }}
          startAdornment={
            <InputAdornment position="start">
              <img src={FilterIcon} alt="Filter" style={{ width: "18px", height: "18px", marginRight: "8px" }} />
            </InputAdornment>
          }
        >
          <MenuItem value="all">{categoryFilter !== "all" ? enumStrings.filters.clearCategory : enumStrings.filters.filterByCategory}</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {(categoryIdToHumanReadableName as any)?.[cat] || cat}
            </MenuItem>
          ))}
        </Select>

        <Select
          className={classes.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as string)}
          variant="outlined"
          displayEmpty
          style={{ color: statusFilter === "all" ? "#9E9E9E" : "#000" }}
          startAdornment={
            <InputAdornment position="start">
              <img src={FilterIcon} alt="Filter" style={{ width: "18px", height: "18px", marginRight: "8px" }} />
            </InputAdornment>
          }
        >
          <MenuItem value="all">{statusFilter !== "all" ? enumStrings.filters.clearStatus : enumStrings.filters.filterByActivationStatus}</MenuItem>
          <MenuItem value="active">{enumStrings.filters.active}</MenuItem>
          <MenuItem value="inactive">{enumStrings.filters.inactive}</MenuItem>
        </Select>

        {/* Clear All Filters Button */}
        <Button
          variant="contained"
          onClick={() => {
            setSearchInput("");
            setCategoryFilter("all");
            setStatusFilter("all");
          }}
          style={{
            backgroundColor: (theme.palette as any).delete.main,
            color: "#FFFFFF",
            borderRadius: "22px",
            padding: "8px 12px",
            fontFamily: "Montserrat",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "15px",
            textTransform: "none",
            textAlign: "center",
            boxShadow: "none",
            minWidth: "unset",
            whiteSpace: "nowrap",
          }}
        >
          CLEAR ALL FILTERS
        </Button>
      </div>
      {/* Desktop table */}
      <div className={classes.desktopTableWrapper}>
        <CommonTable<Bongus>
          data={filteredData}
          columns={columns}
          defaultSortColumn="name"
          defaultSortOrder="asc"
          hideSearch={true}
          rowKeyField="id"
          renderCell={renderCell}
        />
      </div>

      {/* Mobile card list */}
      <div className={classes.mobileCardList}>
        {filteredData.length === 0 ? (
          <p style={{ textAlign: "center", color: "#666", padding: "40px 0" }}>
            {enumStrings.manageMemberProfile.loadingChamberMembers}
          </p>
        ) : (
          filteredData.map((member) => {
            const isActive = !!member.activeMemberProfileAndPerksBannersSettings?.activationInfo;
            const hasReactivation = !!member.activeMemberProfileAndPerksBannersSettings?.reactivationInfo;

            // Avatar initials: first letter of each word, max 3
            const initials = (member.name || "")
              .split(" ")
              .filter(Boolean)
              .map((w: string) => w[0] || "")
              .join("")
              .slice(0, 3)
              .toUpperCase();

            // Status
            let statusText: string;
            let statusColor: string;
            if (!isActive) {
              statusText = enumStrings.manageMemberProfile.notActivatedStatus;
              statusColor = "#8B1A1A";
            } else if (hasReactivation) {
              const reactivationDate = member.activeMemberProfileAndPerksBannersSettings!.reactivationInfo!.startDateUtcMilli;
              statusText = `${enumStrings.manageMemberProfile.reactivatedStatus} · ${getLocalTimestamp(reactivationDate, true)}`;
              statusColor = "#4CAF50";
            } else {
              statusText = enumStrings.manageMemberProfile.activatedStatus;
              statusColor = "#4CAF50";
            }

            // Fee
            const annualFee = isActive
              ? member.activeMemberProfileAndPerksBannersSettings?.activationInfo?.memberProfileAndPerkBannerAnnualBannerFeeInDollarsAtTimeOfActivation
              : chamberInfo?.chamberEngageSettings?.memberProfileAndPerkBannerAnnualBannerFeeInDollars;

            // Period & days remaining
            let periodText: string | null = null;
            let daysRemainingText: string | null = null;
            let daysRemainingColor = "#4CAF50";
            let isExpiresSoon = false;

            if (isActive && member.activeMemberProfileAndPerksBannersSettings?.activationInfo) {
              const startMilli = member.activeMemberProfileAndPerksBannersSettings.activationInfo.startDateUtcMilli;
              const endMilli = getExpiryUtcMilliTimestampThatIsSpecifiedMonthsAfterSpecifiedAdvertisingStartDateUtcMilli({
                advertisingStartDateUtcMilli: startMilli,
                months: numberOfMonthsTillExpiry,
              });
              periodText = `${getLocalTimestamp(startMilli, true)} → ${getLocalTimestamp(endMilli, true)}`;
              const totalDaysLeft = Math.max(0, Math.ceil((new Date(endMilli).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
              isExpiresSoon = totalDaysLeft <= 20;
              daysRemainingColor = isExpiresSoon ? "#E53935" : "#4CAF50";
              daysRemainingText = enumStrings.manageMemberProfile.timeRemainingDays.replace("{{days}}", String(totalDaysLeft));
            }

            return (
              <div key={member.id} className={classes.memberCard}>
                {/* Card header */}
                <div className={classes.cardHeader}>
                  {member.logo ? (
                    <img
                      src={member.logo}
                      alt={member.name}
                      style={{ width: "56px", height: "56px", borderRadius: "12px", objectFit: "contain", flexShrink: 0 }}
                    />
                  ) : (
                    <div
                      className={classes.cardAvatar}
                      style={{
                        backgroundColor: isActive ? "#24272C" : "#FEE8E8",
                        color: isActive ? "#FFFFFF" : "#8B1A1A",
                      }}
                    >
                      {initials}
                    </div>
                  )}
                  <div>
                    <div className={classes.cardCompanyName}>{member.name}</div>
                    <div className={classes.cardStatus} style={{ color: statusColor }}>
                      {statusText}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className={classes.cardDivider} />

                {/* Info rows */}
                <div className={classes.cardInfoRow}>
                  <span className={classes.cardInfoLabel}>{enumStrings.manageChamberEngage.fee}</span>
                  <span className={classes.cardInfoValue}>${annualFee}/yr</span>
                </div>

                {isActive && periodText ? (
                  <>
                    <div className={classes.cardInfoRow}>
                      <span className={classes.cardInfoLabel}>{enumStrings.manageMemberProfile.period}</span>
                      <span className={classes.cardInfoValue} style={{ maxWidth: "60%" }}>{periodText}</span>
                    </div>
                    <div className={classes.cardInfoRow}>
                      <span className={classes.cardInfoLabel}>
                        {isExpiresSoon ? enumStrings.manageMemberProfile.expiresSoon : enumStrings.manageMemberProfile.daysRemaining}
                      </span>
                      <span style={{ color: daysRemainingColor, fontWeight: 700, fontSize: "14px" }}>
                        {daysRemainingText}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className={classes.cardAwaitingText}>
                    {enumStrings.manageMemberProfile.awaitingActivation}
                  </p>
                )}

                {/* Action buttons */}
                <div className={classes.cardButtonsRow}>
                  {isActive ? (
                    <>
                      <Button
                        variant="contained"
                        className={classes.cardButton}
                        style={{ backgroundColor: theme.palette.primary.main, color: "white" }}
                        onClick={() => handleOpenReactivateForMemberModal({ chamberMember: member, modalType: "newRe" })}
                      >
                        {enumStrings.manageChamberEngage.buttons.reactivate}
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.cardButton}
                        style={{ backgroundColor: "#24272C", color: "white" }}
                        onClick={() => handleOpenDeactivateForMemberModal({ chamberMember: member })}
                      >
                        {enumStrings.manageChamberEngage.buttons.deactivate}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      style={{
                        width: "100%",
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        borderRadius: "10px",
                        padding: "14px 16px",
                        fontWeight: "bold",
                        fontSize: "15px",
                        textTransform: "none",
                        boxShadow: "none",
                      }}
                      onClick={() => history.push(RedirectUri.chamberEngageActivateFeaturedMember, { preselectedMemberId: member.id })}
                    >
                      {enumStrings.manageMemberProfile.activateFeaturedBanner}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <SuccessDialog
        open={dialogState.open}
        onClose={() => setDialogState(prev => ({ ...prev, open: false }))}
        message={dialogState.message}
        variant={dialogState.variant}
        autoDismissMs={5000}
      />
    </Box>
  );
}

const ManageMemberProfileAndPerksBanners = (): ReactElement => {
  const classes = useStyles();
  const location = useLocation();
  const {
    state: { userType },
  } = useContext(UserTypeContext);

  const [allChamberMembersForChamberData, setAllChamberMembersForChamberData] =
    React.useState<AllTableAllChamberMembersForChamberDataType>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [dialogState, setDialogState] = useState<{ open: boolean; message: string; variant: "success" | "error" }>({ open: false, message: "", variant: "success" });
  const chamberIdToUse =
    useGetChamberIdToUseToPullAllChamberMembersForRootOrChamberAdmin();

  const { data: allChamberMembersForChamber, loading } =
    useGetAllChamberMembers_RootAndChamberAdminOnlyQuery({
      variables: { chamberId: chamberIdToUse || "" },
      skip: !chamberIdToUse,
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    const getAllChamberMembersForChamberstats = async () => {
      if (!(allChamberMembersForChamber?.getAllChamberMembers && !loading)) {
        setIsLoading(true);
        setAllChamberMembersForChamberData([]);
        return;
      }

      const allChamberMembersForChamberArr: AllTableAllChamberMembersForChamberDataType =
        [];

      for (const chamberMember of allChamberMembersForChamber?.getAllChamberMembers ||
        []) {
        allChamberMembersForChamberArr.push({
          ...chamberMember,
          statusForTable: chamberMember
            .activeMemberProfileAndPerksBannersSettings?.activationInfo
            ? (chamberMember.activeMemberProfileAndPerksBannersSettings?.reactivationInfo
              ? enumStrings.manageMemberProfile.reactivatedStatus
              : enumStrings.manageMemberProfile.activatedStatus)
            : enumStrings.manageMemberProfile.notActivatedStatus,
          annualFeeForTable:
            chamberMember.activeMemberProfileAndPerksBannersSettings
              ?.activationInfo
              ?.memberProfileAndPerkBannerAnnualBannerFeeInDollarsAtTimeOfActivation,
          startDateForTable:
            chamberMember.activeMemberProfileAndPerksBannersSettings
              ?.activationInfo?.startDateUtcMilli,
          expiryDateForTable: chamberMember
            .activeMemberProfileAndPerksBannersSettings?.activationInfo
            ?.startDateUtcMilli
            ? getExpiryUtcMilliTimestampThatIsSpecifiedMonthsAfterSpecifiedAdvertisingStartDateUtcMilli(
              {
                advertisingStartDateUtcMilli:
                  chamberMember.activeMemberProfileAndPerksBannersSettings
                    ?.activationInfo?.startDateUtcMilli || 0,
                months: numberOfMonthsTillExpiry,
              }
            )
            : undefined,
        });
      }

      setAllChamberMembersForChamberData(
        allChamberMembersForChamberArr
          .slice()
          // sort alphabetically by name
          .sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          })
          // then sort by active status
          .sort((a, b) => {
            if (
              a.activeMemberProfileAndPerksBannersSettings?.activationInfo &&
              b.activeMemberProfileAndPerksBannersSettings?.activationInfo
            ) {
              return 0;
            }
            if (
              a.activeMemberProfileAndPerksBannersSettings?.activationInfo &&
              !b.activeMemberProfileAndPerksBannersSettings?.activationInfo
            ) {
              return -1;
            }
            if (
              !a.activeMemberProfileAndPerksBannersSettings?.activationInfo &&
              b.activeMemberProfileAndPerksBannersSettings?.activationInfo
            ) {
              return 1;
            }
            return 0;
          })
      );

      // set allChamberMembersIdToName
      const allChamberMembersIdToNameObj = {} as {
        [key: string]: Bongus;
      };
      for (const chamberMemberObj of allChamberMembersForChamber.getAllChamberMembers) {
        allChamberMembersIdToNameObj[chamberMemberObj.id] =
          chamberMemberObj as Bongus;
      }

      setIsLoading(false);
    };

    getAllChamberMembersForChamberstats();
  }, [
    allChamberMembersForChamber,
    allChamberMembersForChamber?.getAllChamberMembers,
    loading,
  ]);

  const enumStrings = useEnumStrings();

  const rootAdminObj = // @ts-ignore
    enumStrings?.groups[userType][
    RedirectUri.chamberEngageManageMemberProfileAndPerkBanners
    ];

  const history = useHistory();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  const chamberInfo = useGetChamberFromUserIdInAuth() as
    | GetUser_RootAdminChamberAdminQuery["getUser"]["chamberInfo"]
    | undefined;

  // Check if we're on the activate page or manage page
  const isActivatePage = location.pathname === RedirectUri.chamberEngageActivateFeaturedMember;
  const isManagePage = location.pathname === RedirectUri.chamberEngageManageFeaturedMemberActivations;

  // Get page title based on route
  const pageTitle = isActivatePage
    ? enumStrings.manageMemberProfile.activateEnhancedTitle
    : isManagePage
    ? enumStrings.manageMemberProfile.manageFeaturedMemberTitle
    : rootAdminObj.title;

  const pageDescription = isActivatePage
    ? enumStrings.manageMemberProfile.activateEnhancedDescription
    : isManagePage
    ? null
    : rootAdminObj.description;

  return (
    <div style={{ overflowX: "auto" }}>
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <h1 className={classes.title}>
            {pageTitle}
            {!isActivatePage && (location?.state as any)?.chamberName &&
              ` for ${(location?.state as any)?.chamberName}`}
          </h1>
        </div>
        <p className={classes.description}>
          {isManagePage ? (
            <>
              {enumStrings.manageMemberProfile.manageFeaturedMemberDescPart1}
            </>
          ) : (
            <>
              {pageDescription} {!isActivatePage && enumStrings.watchVideoToLearnHow}
              {isActivatePage && (
                <><br /><br />{enumStrings.manageMemberProfile.activateEnhancedDescriptionLine2}</>
              )}
            </>
          )}
        </p>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/28a96f32-ce4d-48b8-bf2b-6cf6a2e75326"
          />
          {userType !== UserType.RootAdmin && (
            <ExportButton
              customOnClick={() =>
                openInstructionPdf(
                  isActivatePage
                    ? "Activate Featured Member Profile & Perks Banners.pdf"
                    : "Manage Featured Member Banner Activation.pdf"
                )
              }
              disablePadding
              icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
              label={enumStrings.manageChamberEngage.viewInstructions}
            />
          )}
        </div>
      </div>
      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
      />
      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <SuccessDialog
            open={dialogState.open}
            onClose={() => setDialogState(prev => ({ ...prev, open: false }))}
            message={dialogState.message}
            variant={dialogState.variant}
            autoDismissMs={5000}
          />
          {isActivatePage ? (
            <ActivationForm
              chamberInfo={chamberInfo}
              allChamberMembers={allChamberMembersForChamberData}
              isLoadingMembers={isLoading}
              refetch={() => {
                // Refetch necessary queries
                // In this implementation, rely on Apollo cache updates or component re-render
                // Trigger a refetch if needed by invalidating queries.
                // For now, simple re-render might suffice if cache is updated by mutation (refetchQueries).
                // The mutation already refetches 'GetAllChamberMembers_RootAndChamberAdminOnlyDocument' in other parts of file.
                // We can just rely on that.
              }}
            />
          ) : (
            <>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  {allChamberMembersForChamberData &&
                    allChamberMembersForChamberData.length > 0 ? (
                    <FullPageManageAllChamberMembersForChamberTable
                      allChamberMembersForChamberData={
                        allChamberMembersForChamberData
                      }
                    />
                  ) : (
                    <div
                      style={{
                        color: "black",
                        textAlign: "center",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "auto",
                      }}
                    >
                      {enumStrings.manageMemberProfile.loadingChamberMembers}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageMemberProfileAndPerksBanners;
